import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChatCircleDots, X, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBackgroundRefresh } from "../lib/refresh";
import { formatTime, formatDate, apiError } from "../lib/format";
import { toast } from "sonner";
import { AttachButton, PendingAttachment, MessageAttachment } from "../lib/chat";

/**
 * Floating chat widget for clients — single conversation with their assigned advisor.
 * Shows a pulsing FAB bottom-right; click opens a slide-over panel from the right.
 */
export default function FloatingChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [pending, setPending] = useState(null);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const stickBottomRef = useRef(true);
  const PAGE_SIZE = 30;

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  const mergeMessages = (prev, incoming) => {
    const seen = new Map();
    for (const m of prev) seen.set(m.id, m);
    for (const m of incoming) seen.set(m.id, m);
    const arr = Array.from(seen.values());
    arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return arr;
  };

  // Poll conversation summary (for unread badge) every 5s
  const loadConversations = useCallback(async () => {
    if (!user || user.role !== "client") return;
    try {
      const { data } = await api.get("/chat/conversations");
      const first = data[0] || null;
      setConv(first);
      setUnread(first?.unread || 0);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== "client") return;
    loadConversations();
    const i = setInterval(loadConversations, 5000);
    return () => { clearInterval(i); };
  }, [user, loadConversations]);

  // Refresh immediately on visibility / push — no 5s wait when user reopens the app.
  useBackgroundRefresh(loadConversations);

  // Poll messages when open
  useEffect(() => {
    if (!open || !conv) return;
    let cancelled = false;
    let first = true;
    setMessages([]);
    setHasMore(false);
    stickBottomRef.current = true;
    let lastSeenISO = null;
    const initial = async () => {
      try {
        const { data } = await api.get(`/chat/conversations/${conv.id}/messages?limit=${PAGE_SIZE}`);
        if (cancelled) return;
        const arr = (data?.messages || []).filter((m) => m.conversation_id === conv.id);
        setMessages(arr);
        setHasMore(!!data?.has_more);
        if (arr.length) lastSeenISO = arr[arr.length - 1].created_at;
        setTimeout(scrollToBottom, 30);
        setUnread(0);
        first = false;
      } catch {}
    };
    const poll = async () => {
      if (first) return;
      try {
        const url = lastSeenISO
          ? `/chat/conversations/${conv.id}/messages?after=${encodeURIComponent(lastSeenISO)}`
          : `/chat/conversations/${conv.id}/messages?limit=${PAGE_SIZE}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        const incoming = (data?.messages || []).filter((m) => m.conversation_id === conv.id);
        if (incoming.length === 0) return;
        setMessages((prev) => {
          const wasNear = stickBottomRef.current;
          const prevForConv = prev.filter((m) => m.conversation_id === conv.id);
          const merged = mergeMessages(prevForConv, incoming);
          if (merged.length > prevForConv.length && wasNear) setTimeout(scrollToBottom, 30);
          return merged;
        });
        const newest = incoming[incoming.length - 1];
        if (!lastSeenISO || new Date(newest.created_at) > new Date(lastSeenISO)) lastSeenISO = newest.created_at;
        setUnread(0);
      } catch {}
    };
    initial();
    const i = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(i); };
  }, [open, conv?.id]); // eslint-disable-line

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !pending) || !conv) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/conversations/${conv.id}/messages`, {
        text: text.trim(),
        attachment_id: pending?.id || null,
      });
      setMessages((prev) => mergeMessages(prev, [data]));
      setText("");
      setPending(null);
      stickBottomRef.current = true;
      setTimeout(scrollToBottom, 30);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const loadOlder = async () => {
    if (!conv || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const convId = conv.id;
    const oldest = messages[0];
    const scrollEl = scrollRef.current;
    const beforeHeight = scrollEl ? scrollEl.scrollHeight : 0;
    const beforeTop = scrollEl ? scrollEl.scrollTop : 0;
    try {
      const { data } = await api.get(
        `/chat/conversations/${convId}/messages?before=${encodeURIComponent(oldest.created_at)}&limit=${PAGE_SIZE}`
      );
      const older = (data?.messages || []).filter((m) => m.conversation_id === convId);
      setHasMore(!!data?.has_more);
      if (older.length > 0) {
        setMessages((prev) => mergeMessages(older, prev));
        // Preserve viewport: keep distance from the bottom stable
        setTimeout(() => {
          if (!scrollEl) return;
          const newHeight = scrollEl.scrollHeight;
          scrollEl.scrollTop = beforeTop + (newHeight - beforeHeight);
        }, 30);
      }
    } catch {}
    finally {
      setLoadingMore(false);
    }
  };

  if (!user || user.role !== "client") return null;

  const advisor = conv?.partner;

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-fab"
          className="fixed bottom-6 right-6 z-40 group"
          aria-label="Abrir chat con asesor"
        >
          <span className="absolute inset-0 bg-burnt/40 animate-ping rounded-sm" />
          <span className="relative flex items-center gap-3 h-14 pl-5 pr-6 bg-burnt text-bg font-display font-bold shadow-[0_8px_24px_rgba(194,89,52,0.4)] hover:bg-burnt-hover transition-all group-hover:-translate-y-0.5">
            <ChatCircleDots size={22} weight="duotone" />
            <span className="hidden sm:inline">Chat con asesor</span>
            <span className="sm:hidden">Chat</span>
            {unread > 0 && (
              <span
                data-testid="chat-fab-badge"
                className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 bg-forest text-bg text-xs font-mono flex items-center justify-center"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </span>
        </button>
      )}

      {/* Slide-over panel */}
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <aside
            data-testid="chat-panel"
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[420px] bg-surface border-l border-hairline flex flex-col animate-fade-up shadow-2xl"
          >
            {/* Header */}
            <div className="h-16 border-b border-hairline px-5 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-forest text-bg flex items-center justify-center font-display font-bold flex-shrink-0">
                  {advisor?.name?.[0]?.toUpperCase() || "A"}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold truncate">{advisor?.name || "Asesor de UNIK"}</div>
                  <div className="label-mono flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-success rounded-full inline-block" />
                    Asesor en línea
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                className="p-2 hover:bg-bg"
                aria-label="Cerrar chat"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            {!conv ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-inkMuted">
                <ChatCircleDots size={40} weight="duotone" />
                <div className="font-display text-lg font-bold mt-3 text-ink">Aún no hay asesor disponible</div>
                <div className="text-sm mt-1">Un administrador debe asignarte un asesor.</div>
              </div>
            ) : (
              <>
                <div
                  ref={scrollRef}
                  onScroll={() => { stickBottomRef.current = isNearBottom(); }}
                  className="flex-1 overflow-auto p-5 space-y-2 bg-bg"
                  data-testid="chat-fab-scroll"
                >
                  {hasMore && (
                    <div className="flex justify-center pb-3">
                      <button
                        onClick={loadOlder}
                        disabled={loadingMore}
                        data-testid="chat-fab-load-older"
                        className="h-8 px-3 border border-hairline bg-surface text-[10px] font-mono uppercase tracking-widest hover:bg-forest hover:text-bg hover:border-forest disabled:opacity-50 disabled:cursor-wait"
                      >
                        {loadingMore ? "Cargando…" : "↑ Cargar mensajes anteriores"}
                      </button>
                    </div>
                  )}
                  {messages.length === 0 && (
                    <div className="text-center text-inkMuted text-sm py-8">Envía el primer mensaje a tu asesor.</div>
                  )}
                  {messages.map((m, i) => {
                    const mine = m.sender_id === user.id;
                    const prev = messages[i - 1];
                    const showDate = !prev || formatDate(prev.created_at) !== formatDate(m.created_at);
                    return (
                      <React.Fragment key={m.id}>
                        {showDate && (
                          <div className="text-center label-mono my-3">{formatDate(m.created_at)}</div>
                        )}
                        <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] px-3.5 py-2 space-y-2 ${mine ? "bg-forest text-bg" : "bg-surface border border-hairline"}`}>
                            {m.attachment && <MessageAttachment attachment={m.attachment} mine={mine} />}
                            {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                            <div className={`text-[10px] font-mono ${mine ? "text-bg/60" : "text-inkMuted"}`}>
                              {formatTime(m.created_at)}{mine && (m.read ? " · leído" : " · enviado")}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                <form onSubmit={send} className="border-t border-hairline p-3 flex flex-col gap-2">
                  {pending && (
                    <PendingAttachment att={pending} onRemove={() => setPending(null)} />
                  )}
                  <div className="flex gap-2 items-end">
                    <AttachButton onFile={setPending} disabled={sending} testid="chat-fab-attach" />
                    <textarea
                      data-testid="chat-fab-input"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send(e);
                        }
                      }}
                      rows={1}
                      placeholder={pending ? "Mensaje opcional…" : "Escribe un mensaje — Enter envía, Shift+Enter salto"}
                      className="flex-1 min-h-[48px] max-h-[140px] py-3 px-3 border border-hairline bg-surface focus:outline-none focus:border-forest resize-none font-body text-sm leading-normal whitespace-pre-wrap"
                      style={{ fontFamily: 'inherit' }}
                    />
                    <button
                      data-testid="chat-fab-send"
                      className="h-12 px-4 bg-forest text-bg font-semibold flex items-center gap-2 hover:bg-forest-hover disabled:opacity-50 flex-shrink-0"
                      disabled={sending || (!text.trim() && !pending)}
                    >
                      <PaperPlaneTilt size={16} weight="bold" />
                    </button>
                  </div>
                </form>
              </>
            )}
          </aside>
        </>
      )}
    </>
  );
}
